'use client';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import {
    FormEvent,
    FunctionComponent,
    Reducer,
    useCallback,
    useId,
    useMemo,
    useReducer,
} from 'react';
import Button from '../../Button';
import styles from './styles.module.scss';
import Alert from '../../Alert';
import BlueprintIcon from '../../BlueprintIcon';
import Link from 'next/link';
import Image from 'next/image';

type FormState = {
    name: string;
    email: string;
    telephone: string;
    address: string;
    job_title: string;
    isSubmitting: boolean;
    hasSubmitted: boolean;
    error: Error | null;
};

const initialState: FormState = {
    name: '',
    email: '',
    telephone: '',
    address: '',
    job_title: '',
    isSubmitting: false,
    hasSubmitted: false,
    error: null,
};

type FormAction =
    | { type: 'name changed'; payload: string }
    | { type: 'email changed'; payload: string }
    | { type: 'telephone changed'; payload: string }
    | { type: 'address changed'; payload: string }
    | { type: 'job title changed'; payload: string }
    | { type: 'started submitting' }
    | { type: 'submission successful' }
    | { type: 'submission failed'; payload: Error };

const unSubmittedFormReducer: Reducer<FormState, FormAction> = (state, action) => {
    switch (action.type) {
        case 'name changed': {
            return { ...state, name: action.payload };
        }
        case 'email changed': {
            return { ...state, email: action.payload };
        }
        case 'telephone changed': {
            return { ...state, telephone: action.payload };
        }
        case 'address changed': {
            return { ...state, address: action.payload };
        }
        case 'job title changed': {
            return { ...state, job_title: action.payload };
        }
        case 'started submitting': {
            return { ...state, isSubmitting: true, error: null };
        }
    }
    return state;
};

const submittingFormReducer: Reducer<FormState, FormAction> = (state, action) => {
    switch (action.type) {
        case 'submission failed': {
            return { ...state, isSubmitting: false, error: action.payload };
        }
        case 'submission successful': {
            return { ...state, isSubmitting: false, error: null, hasSubmitted: true };
        }
    }
    return state;
};

const formReducer: Reducer<FormState, FormAction> = (state, action) => {
    if (state.isSubmitting === false && state.hasSubmitted === false) {
        return unSubmittedFormReducer(state, action);
    }
    if (state.isSubmitting === true) {
        return submittingFormReducer(state, action);
    }
    return state;
};

const parseError = (error: string): string | null => {
    try {
        const parsed = JSON.parse(error);
        if (parsed.safe && Array.isArray(parsed.safe)) {
            return parsed.safe.join(', ');
        }
    } catch (_error) {
        return null;
    }
    return null;
};

const ClaimProfileSubmittedPage: FunctionComponent<{
    educator: PublicEducator;
    submittedName: string;
}> = ({ educator, submittedName }) => {
    return (
        <section className={styles.claim_profile_container}>
            <BlueprintIcon icon={'Envelope'} width={70} height={70} />
            <h1>Thanks for your claim!</h1>
            <p>
                Thank you <b>{submittedName}</b> for getting in touch. We have received your profile
                claim and someone will be in touch soon to start the verification process.
            </p>
            <Link href={`/${educator.slug}`}>
                <Button className={styles.submit} preset="primary">
                    Close
                </Button>
            </Link>
        </section>
    );
};

export const ClaimProfileFormPage: FunctionComponent<{ educator: PublicEducator }> = ({
    educator,
}) => {
    const id = useId();
    const [formState, dispatch] = useReducer(formReducer, initialState);
    const { name, email, telephone, address, job_title, isSubmitting, hasSubmitted, error } =
        formState;

    const claimPayload = useMemo(() => {
        return {
            fields: {
                'Educator Ref': educator._id,
                'Educator Name': educator.name,
                'Educator Slug': educator.slug,
                Name: name,
                'Job Title / Role': job_title,
                Email: email,
                Phone: telephone,
                Address: address,
                Status: 'New',
            },
        };
    }, [address, educator._id, educator.name, educator.slug, email, job_title, name, telephone]);

    const onSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (isSubmitting) return;
            dispatch({ type: 'started submitting' });
            try {
                const response = await fetch(
                    `https://api.airtable.com/v0/${process.env.NEXT_PUBLIC_AIRTABLE_PROFILE_CLAIM_TABLE}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${process.env.NEXT_PUBLIC_AIRTABLE_API_KEY}`,
                        },
                        body: JSON.stringify(claimPayload),
                    }
                );
                if (response.ok === false) {
                    const error = parseError(await response.text());
                    throw new Error(error || 'An unknown error occurred submitting the form');
                }
                dispatch({ type: 'submission successful' });
            } catch (error) {
                dispatch({
                    type: 'submission failed',
                    payload: error instanceof Error ? error : new Error(String(error)),
                });
            }
        },
        [claimPayload, isSubmitting]
    );

    if (hasSubmitted) {
        return <ClaimProfileSubmittedPage educator={educator} submittedName={name} />;
    }

    return (
        <section className={styles.claim_profile_container}>
            <Image
                src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/logos/logo-resized.svg`}
                alt="Cademy logo"
                width={250}
                height={40}
            />
            <form
                id={id}
                name={`Claim ${educator.name} profile`}
                className={styles.form}
                autoComplete="on"
                onSubmit={onSubmit}
                aria-labelledby={`${id}-header`}
                aria-describedby={`${id}-header-helptext`}
            >
                <header>
                    <h1 id={`${id}-header`}>Claim Profile</h1>
                    <p id={`${id}-header-helptext`}>
                        Submit your claim for <b>{educator.name}</b> by filling in the form below.
                    </p>
                </header>
                {error ? <Alert preset="danger">{error.message}</Alert> : null}
                <div className={styles.fieldContainer}>
                    <label htmlFor={`${id}-name`}>Name</label>
                    <input
                        id={`${id}-name`}
                        type="text"
                        required={true}
                        name="name"
                        disabled={isSubmitting}
                        onChange={(event) =>
                            dispatch({ type: 'name changed', payload: event.target.value })
                        }
                    />
                </div>
                <div className={styles.fieldContainer}>
                    <label htmlFor={`${id}-email`}>Email</label>
                    <input
                        id={`${id}-email`}
                        type="email"
                        required={true}
                        name="email"
                        disabled={isSubmitting}
                        onChange={(event) =>
                            dispatch({ type: 'email changed', payload: event.target.value })
                        }
                    />
                </div>
                <div className={styles.fieldContainer}>
                    <label htmlFor={`${id}-phone`}>Phone</label>
                    <input
                        id={`${id}-phone`}
                        type="tel"
                        required={true}
                        name="phone"
                        disabled={isSubmitting}
                        onChange={(event) =>
                            dispatch({ type: 'telephone changed', payload: event.target.value })
                        }
                    />
                </div>
                <div className={styles.fieldContainer}>
                    <label htmlFor={`${id}-address`}>Address</label>
                    <input
                        id={`${id}-address`}
                        type="text"
                        required={true}
                        name="address"
                        disabled={isSubmitting}
                        onChange={(event) =>
                            dispatch({ type: 'address changed', payload: event.target.value })
                        }
                    />
                </div>
                <div className={styles.fieldContainer}>
                    <label htmlFor={`${id}-job_title`}>Job Title / Role</label>
                    <input
                        id={`${id}-job_title`}
                        type="text"
                        required={true}
                        name="job_title"
                        disabled={isSubmitting}
                        onChange={(event) =>
                            dispatch({ type: 'job title changed', payload: event.target.value })
                        }
                    />
                </div>
                <Button
                    className={styles.submit}
                    preset="primary"
                    type="submit"
                    disabled={isSubmitting}
                >
                    Submit
                </Button>
            </form>
        </section>
    );
};

export default ClaimProfileFormPage;
